import React, { useState, useEffect } from 'react';
import { Alert, Button, Modal, Space, Typography } from 'antd';
import { CloseCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { useGetMyShiftClosingsQuery } from '../../services/shiftClosingApi';
import { ShiftClosingListDto } from '../../types/cashRegister';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;

const RejectedShiftClosingAlert: React.FC = () => {
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const [dismissedClosings, setDismissedClosings] = useState<string[]>([]);
  const [selectedClosing, setSelectedClosing] = useState<ShiftClosingListDto | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Get the current user's rejected shift closings (last 7 days). Uses /mine — user and
  // restaurant come from the JWT — because /history is admin-only (403 for Cajero) and
  // used to leak every employee's rejections to whoever rendered this alert.
  const { data: history } = useGetMyShiftClosingsQuery({
    fromDate: dayjs().subtract(7, 'days').toISOString(),
    toDate: dayjs().toISOString(),
    status: 'Rejected',
  });

  // Load dismissed closings from localStorage
  useEffect(() => {
    const dismissed = localStorage.getItem('dismissedRejectedClosings');
    if (dismissed) {
      setDismissedClosings(JSON.parse(dismissed));
    }
  }, []);

  // Filter out dismissed closings
  const rejectedClosings = history?.filter(
    closing => !dismissedClosings.includes(closing.id)
  ) || [];

  const handleDismiss = (closingId: string) => {
    const newDismissed = [...dismissedClosings, closingId];
    setDismissedClosings(newDismissed);
    localStorage.setItem('dismissedRejectedClosings', JSON.stringify(newDismissed));
  };

  const handleViewDetails = (closing: ShiftClosingListDto) => {
    setSelectedClosing(closing);
    setModalVisible(true);
  };

  if (rejectedClosings.length === 0) {
    return null;
  }

  return (
    <>
      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size="small">
        {rejectedClosings.map(closing => (
          <Alert
            key={closing.id}
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
            message={
              <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                <Text strong>Cierre de Turno Rechazado</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {formatInTimeZone(closing.createdAt, 'DD/MM/YYYY HH:mm')}
                </Text>
              </Space>
            }
            description={
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {closing.reviewNotes && (
                  <Paragraph style={{ marginBottom: 0 }}>
                    <Text strong>Motivo: </Text>
                    <Text>{closing.reviewNotes}</Text>
                  </Paragraph>
                )}
                <Text type="secondary">
                  Revisado por: {closing.reviewedByUserName || 'Administrador'}
                </Text>
              </Space>
            }
            action={
              <Space direction={isMobile ? 'vertical' : 'horizontal'} size="small">
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handleViewDetails(closing)}
                >
                  Ver Detalles
                </Button>
                <Button
                  size="small"
                  onClick={() => handleDismiss(closing.id)}
                >
                  Entendido
                </Button>
              </Space>
            }
            closable={false}
            style={{ marginBottom: 0 }}
          />
        ))}
      </Space>

      <Modal
        title="Detalle del Cierre Rechazado"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedClosing(null);
        }}
        footer={[
          <Button
            key="dismiss"
            type="primary"
            onClick={() => {
              if (selectedClosing) {
                handleDismiss(selectedClosing.id);
              }
              setModalVisible(false);
              setSelectedClosing(null);
            }}
          >
            Entendido
          </Button>,
        ]}
        width={isMobile ? '100%' : 600}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : {}}
      >
        {selectedClosing && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              type="error"
              message="Este cierre de turno fue rechazado"
              description={
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text>
                    <strong>Fecha del cierre:</strong> {formatInTimeZone(selectedClosing.createdAt, 'DD/MM/YYYY HH:mm')}
                  </Text>
                  <Text>
                    <strong>Revisado por:</strong> {selectedClosing.reviewedByUserName || 'Administrador'}
                  </Text>
                  {selectedClosing.reviewedAt && (
                    <Text>
                      <strong>Fecha de revisión:</strong> {formatInTimeZone(selectedClosing.reviewedAt, 'DD/MM/YYYY HH:mm')}
                    </Text>
                  )}
                </Space>
              }
              showIcon
            />

            {selectedClosing.reviewNotes && (
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Motivo del Rechazo:</Text>
                <Alert
                  type="warning"
                  message={selectedClosing.reviewNotes}
                  showIcon
                />
              </div>
            )}

            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Resumen del Cierre:</Text>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Text>Total Contado: ${selectedClosing.countedTotal.toFixed(2)}</Text>
                <Text>Total Sistema: ${selectedClosing.systemTotal.toFixed(2)}</Text>
                <Text type={selectedClosing.totalDifference === 0 ? 'success' : 'danger'}>
                  Diferencia: ${selectedClosing.totalDifference.toFixed(2)}
                </Text>
              </Space>
            </div>

            <Alert
              type="info"
              message="¿Qué hacer ahora?"
              description="Por favor, contacta a tu supervisor o administrador para revisar este cierre. Es posible que necesites proporcionar información adicional o aclaraciones sobre las transacciones del turno."
              showIcon
            />
          </Space>
        )}
      </Modal>
    </>
  );
};

export default RejectedShiftClosingAlert;
